<template>
  <div class="task-add-page">
    <div class="page-header">
      <span class="page-title">{{ isEdit ? '编辑任务' : '新增任务' }}</span>
      <a-button type="primary" @click="goBack">
        返回
        <template #icon><ArrowLeftOutlined /></template>
      </a-button>
    </div>

    <div class="page-body">
      <!-- 左侧：任务信息 -->
      <div class="left-panel">
        <a-form
          :model="formData"
          :rules="rules"
          ref="formRef"
          layout="vertical"
          class="task-form"
        >
          <!-- 基础信息 -->
          <div class="form-section">
            <div class="section-title">基础信息</div>
            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item label="车辆编码" name="vehicleId" required>
                  <a-select
                    v-model:value="formData.vehicleId"
                    show-search
                    placeholder="请搜索车辆编码"
                    :filter-option="false"
                    :not-found-content="vehicleFetching ? undefined : null"
                    :options="vehicleOptions"
                    :field-names="{ label: 'label', value: 'id' }"
                    @search="handleVehicleSearch"
                    @change="handleVehicleChange"
                    allow-clear
                  ></a-select>
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item label="车辆识别码">
                  <a-input v-model:value="formData.vin" placeholder="选择车辆后自动填充" disabled />
                </a-form-item>
              </a-col>
            </a-row>
            <a-form-item label="运营企业所在地" required>
              <a-row :gutter="16">
                <a-col :span="12">
                  <a-form-item name="cityId" no-style>
                    <a-select
                      v-model:value="formData.cityId"
                      placeholder="请选择城市"
                      :options="cityOptions"
                      :field-names="{ label: 'name', value: 'id' }"
                      :filter-option="(input, option) => (option.name || '').includes(input)"
                      show-search
                      allow-clear
                    ></a-select>
                  </a-form-item>
                </a-col>
                <a-col :span="12">
                  <a-form-item name="operatorId" no-style>
                    <a-select
                      v-model:value="formData.operatorId"
                      placeholder="请选择运营企业"
                      :options="operatorOptions"
                      :field-names="{ label: 'name', value: 'id' }"
                      show-search
                      :filter-option="(input, option) => (option.name || '').includes(input)"
                      allow-clear
                    ></a-select>
                  </a-form-item>
                </a-col>
              </a-row>
            </a-form-item>
            <a-form-item label="安全员" name="securityUserIds" required>
              <a-select
                v-model:value="formData.securityUserIds"
                mode="multiple"
                placeholder="请选择安全员"
                :options="securityUserOptions"
                :field-names="{ label: 'name', value: 'id' }"
                show-search
                :filter-option="(input, option) => (option.name || '').includes(input)"
                allow-clear
              ></a-select>
            </a-form-item>
            <a-form-item label="运营日期范围" name="operationDateRange" required>
              <a-range-picker
                v-model:value="formData.operationDateRange"
                format="YYYY-MM-DD"
                style="width: 100%"
                :placeholder="['开始日期', '结束日期']"
              />
            </a-form-item>
            <a-form-item label="运营时段" name="operationTimes" required>
              <div class="time-slots">
                <div
                  v-for="(slot, idx) in formData.operationTimes"
                  :key="idx"
                  class="time-slot-row"
                >
                  <a-time-picker
                    v-model:value="slot.start"
                    format="HH:mm"
                    placeholder="开始"
                    :allow-clear="false"
                  />
                  <span class="time-separator">~</span>
                  <a-time-picker
                    v-model:value="slot.end"
                    format="HH:mm"
                    placeholder="结束"
                    :allow-clear="false"
                  />
                  <a-button
                    class="ml10"
                    size="small"
                    danger
                    @click="removeTimeSlot(idx)"
                    :disabled="formData.operationTimes.length <= 1"
                  >
                    删除
                  </a-button>
                </div>
                <a-button type="dashed" size="small" @click="addTimeSlot">
                  + 添加时段
                </a-button>
              </div>
            </a-form-item>
            <a-form-item label="任务描述" name="description">
              <a-textarea
                v-model:value="formData.description"
                placeholder="请输入任务描述"
                :rows="3"
                :maxlength="500"
                show-count
              />
            </a-form-item>
          </div>

          <!-- 线路信息 -->
          <div class="form-section">
            <div class="section-title">路线信息</div>
            <a-form-item label="路线名称" name="routeName" required>
              <a-input v-model:value="formData.routeName" placeholder="请输入路线名称" />
            </a-form-item>
            <a-form-item label="起点" name="originStopId" required>
              <div class="point-row">
                <span class="point-dot start-dot"></span>
                <a-select
                  v-model:value="formData.originStopId"
                  placeholder="请选择起点"
                  :options="stopOptions"
                  :field-names="{ label: 'name', value: 'id' }"
                  :filter-option="(input, option) => (option.name || '').includes(input)"
                  show-search
                  allow-clear
                  class="point-select"
                  @change="updateRouteMarkers"
                ></a-select>
              </div>
            </a-form-item>

            <div class="waypoint-block">
              <div class="waypoint-header">
                <span class="waypoint-label">途经点</span>
                <a-button type="dashed" size="small" @click="addWaypoint">
                  + 添加途经点
                </a-button>
              </div>
              <div class="waypoint-list">
                <div
                  v-for="(point, idx) in formData.waypoints"
                  :key="idx"
                  class="waypoint-row"
                >
                  <span class="waypoint-index">{{ idx + 1 }}</span>
                  <a-select
                    v-model:value="point.stopId"
                    placeholder="请选择途经点"
                    :options="stopOptions"
                    :field-names="{ label: 'name', value: 'id' }"
                    :filter-option="(input, option) => (option.name || '').includes(input)"
                    show-search
                    allow-clear
                    class="waypoint-input"
                    @change="updateRouteMarkers"
                  ></a-select>
                  <div class="waypoint-actions">
                    <a-button
                      size="small"
                      type="text"
                      :disabled="idx === 0"
                      @click="moveWaypoint(idx, -1)"
                      title="上移"
                    >
                      <ArrowUpOutlined />
                    </a-button>
                    <a-button
                      size="small"
                      type="text"
                      :disabled="idx === formData.waypoints.length - 1"
                      @click="moveWaypoint(idx, 1)"
                      title="下移"
                    >
                      <ArrowDownOutlined />
                    </a-button>
                    <a-button
                      size="small"
                      type="text"
                      danger
                      @click="removeWaypoint(idx)"
                      title="删除"
                    >
                      <DeleteOutlined />
                    </a-button>
                  </div>
                </div>
                <div v-if="formData.waypoints.length === 0" class="empty-tip">
                  暂无途经点，点击上方"添加途经点"开始添加
                </div>
              </div>
            </div>

            <a-form-item label="终点" name="destinationStopId" required class="end-point-item">
              <div class="point-row">
                <span class="point-dot end-dot"></span>
                <a-select
                  v-model:value="formData.destinationStopId"
                  placeholder="请选择终点"
                  :options="stopOptions"
                  :field-names="{ label: 'name', value: 'id' }"
                  :filter-option="(input, option) => (option.name || '').includes(input)"
                  show-search
                  allow-clear
                  class="point-select"
                  @change="updateRouteMarkers"
                ></a-select>
              </div>
            </a-form-item>

            <!-- 绘制路线按钮 -->
            <div class="route-action">
              <a-button type="primary" :loading="routeLoading" @click="drawRoute">
                绘制路线
              </a-button>
              <span class="route-tip">选择起点、途经点和终点后点击绘制路线</span>
            </div>
          </div>

          <!-- 提交/取消按钮 -->
          <div class="form-footer">
            <a-button type="primary" :loading="submitLoading" @click="handleSubmit">提交</a-button>
            <a-button class="ml10" @click="goBack">取消</a-button>
          </div>
        </a-form>
      </div>

      <!-- 右侧：地图区域 -->
      <div class="right-panel">
        <div id="task-route-map" class="map-container"></div>
        <div v-if="mapLoading" class="loading-overlay">
          <a-spin size="large" />
          <span class="loading-text">地图加载中...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { message } from "ant-design-vue";
import AMapLoader from "@amap/amap-jsapi-loader";
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
} from "@ant-design/icons-vue";
import dayjs from "dayjs";
import useUserStore from "@/store/modules/user";
import { getVehiclePageList } from "@/api/vehicle/";
import { getOperatorList } from "@/api/settings/operator.js";
import { getSecurityUserList } from "@/api/securityUser/securityUser.js";
import { getStopList } from "@/api/roads/stop.js";
import { getCityList } from "@/api/roads/city.js";
import { getFenceList } from "@/api/roads/fence.js";
import { addTask, getTaskDetail, updateTask } from "@/api/task/task.js";

// 高德地图安全密钥（必须在 AMapLoader.load 之前设置）
window._AMapSecurityConfig = {
  securityJsCode: "cd0893188bd79a43d8e4539aec2e2b2a",
};

const router = useRouter();
const route = useRoute();
const store = useUserStore();
const formRef = ref();
const submitLoading = ref(false);

// 编辑模式：路由带 taskId 时为编辑，否则为新增
const taskId = computed(() => route.query.taskId || null);
const isEdit = computed(() => !!taskId.value);
// 回显标志，用于跳过路线名称自动拼接 watch
let isEchoing = false;

// 车辆搜索相关
const vehicleOptions = ref([]);
const vehicleFetching = ref(false);
let searchTimer = null;

// 运营企业列表
const operatorOptions = ref([]);

// 城市列表
const cityOptions = ref([]);

// 安全员列表
const securityUserOptions = ref([]);

// 站点列表（用于起点/途经点/终点下拉）
const stopOptions = ref([]);

// 地图相关
const mapLoading = ref(false);
const routeLoading = ref(false);
let map = null;
let AMapInstance = null;
let dragRoute = null;
let routeMarkers = [];
// 围栏覆盖物（作为地图背景参考图层，不参与路线绘制清理）
let fenceOverlays = [];

// 解析围栏几何数据
const parseFenceGeometry = (fence) => {
  if (!fence.geometry) return null;
  try {
    return typeof fence.geometry === "string" ? JSON.parse(fence.geometry) : fence.geometry;
  } catch (error) {
    console.error("解析围栏几何数据失败:", error);
    return null;
  }
};

// 创建围栏覆盖物（参考 roads/fence/index.vue 的默认样式）
const createFenceOverlay = (geometry) => {
  const { type, coordinates } = geometry;
  if (type === "Polygon") {
    const ring = coordinates[0] || [];
    const path = ring.map(([lng, lat]) => new AMapInstance.LngLat(lng, lat));
    return new AMapInstance.Polygon({
      path,
      fillColor: "#ff4d4f",
      fillOpacity: 0.15,
      strokeColor: "#ff4d4f",
      strokeWeight: 2,
    });
  } else if (type === "LineString") {
    const path = coordinates.map(([lng, lat]) => new AMapInstance.LngLat(lng, lat));
    return new AMapInstance.Polyline({
      path,
      strokeColor: "#ff4d4f",
      strokeWeight: 2,
    });
  } else if (type === "Point") {
    const [lng, lat] = coordinates;
    return new AMapInstance.Marker({ position: [lng, lat] });
  }
  return null;
};

// 加载围栏列表并绘制到地图上
const loadFences = async () => {
  try {
    const res = await getFenceList();
    if (res.code === 200 && Array.isArray(res.data)) {
      const allBounds = [];
      res.data.forEach((fence) => {
        const geometry = parseFenceGeometry(fence);
        if (!geometry) return;
        const overlay = createFenceOverlay(geometry);
        if (overlay) {
          overlay.setMap(map);
          fenceOverlays.push(overlay);
        }
        // 收集围栏坐标用于视野适配
        const { type, coordinates } = geometry;
        if (type === "Polygon") allBounds.push(...(coordinates[0] || []));
        else if (type === "LineString") allBounds.push(...coordinates);
        else if (type === "Point") allBounds.push(coordinates);
      });
      // 新增模式下，地图视野适配围栏所在区域（编辑模式按路线点位适配）
      if (!isEdit.value && allBounds.length > 0 && map && AMapInstance) {
        const lngs = allBounds.map((c) => c[0]);
        const lats = allBounds.map((c) => c[1]);
        const southWest = [Math.min(...lngs), Math.min(...lats)];
        const northEast = [Math.max(...lngs), Math.max(...lats)];
        map.setBounds(new AMapInstance.Bounds(southWest, northEast), false, [60, 60, 60, 60]);
      }
    }
  } catch (error) {
    console.error("获取围栏列表失败:", error);
  }
};

// 地图 clearMap 后重新挂载围栏覆盖物
const redrawFences = () => {
  if (!map) return;
  fenceOverlays.forEach((o) => {
    try { o.setMap(map); } catch (e) { /* ignore */ }
  });
};

// 解析经纬度字符串为 [lng, lat]
const parseCoordinates = (coordStr) => {
  if (!coordStr || typeof coordStr !== "string") return null;
  const parts = coordStr.split(",").map((s) => parseFloat(s.trim()));
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
  return parts;
};

// 根据站点ID获取坐标
const getStopCoord = (stopId) => {
  if (!stopId) return null;
  const stop = stopOptions.value.find((s) => s.id === stopId);
  if (!stop) return null;
  return parseCoordinates(stop.gcj02Coordinates);
};

// 更新地图上的点位标记（起点/途经点/终点）
const updateRouteMarkers = () => {
  if (!map || !AMapInstance) return;

  // 如果已有拖拽路线，清除地图覆盖物（DragRoute 没有单独 clear 方法）
  if (dragRoute) {
    map.clearMap();
    routeMarkers = [];
    dragRoute = null;
    redrawFences();
  } else {
    // 清除旧标记
    routeMarkers.forEach((m) => {
      try { map.remove(m); } catch (e) { /* ignore */ }
    });
    routeMarkers = [];
  }

  const points = [];

  // 起点（绿色）
  if (formData.originStopId) {
    const coord = getStopCoord(formData.originStopId);
    if (coord) {
      const marker = new AMapInstance.Marker({
        position: coord,
        content:
          '<div style="width:28px;height:28px;border-radius:50%;background:#52c41a;color:#fff;font-size:12px;line-height:28px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>',
        offset: new AMapInstance.Pixel(-14, -14),
        map: map,
      });
      routeMarkers.push(marker);
      points.push(coord);
    }
  }

  // 途经点（蓝色，带序号）
  formData.waypoints.forEach((w, idx) => {
    if (!w.stopId) return;
    const coord = getStopCoord(w.stopId);
    if (coord) {
      const marker = new AMapInstance.Marker({
        position: coord,
        content: `<div style="width:24px;height:24px;border-radius:50%;background:#1890ff;color:#fff;font-size:12px;line-height:24px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${idx + 1}</div>`,
        offset: new AMapInstance.Pixel(-12, -12),
        map: map,
      });
      routeMarkers.push(marker);
      points.push(coord);
    }
  });

  // 终点（红色）
  if (formData.destinationStopId) {
    const coord = getStopCoord(formData.destinationStopId);
    if (coord) {
      const marker = new AMapInstance.Marker({
        position: coord,
        content:
          '<div style="width:28px;height:28px;border-radius:50%;background:#ff4d4f;color:#fff;font-size:12px;line-height:28px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>',
        offset: new AMapInstance.Pixel(-14, -14),
        map: map,
      });
      routeMarkers.push(marker);
      points.push(coord);
    }
  }

  // 自适应视野到路线标记点（仅适配点位标记，不包含围栏）
  if (points.length > 0) {
    map.setFitView(routeMarkers, true, [80, 80, 80, 80]);
  }
};

// 加载高德地图 SDK
const loadAMapSDK = async () => {
  const opts = {
    key: "6588d9e5b6ae0751c799cb4c7b3e30be",
    version: "2.0",
    plugins: ["AMap.ToolBar", "AMap.Scale", "AMap.DragRoute"],
  };
  // 先 reset 再 load，确保安全密钥生效（SPA 中 AMap 可能已被其他页面缓存加载）
  if (typeof AMapLoader.reset === "function") {
    AMapLoader.reset();
  }
  return await AMapLoader.load(opts);
};

// 初始化地图
const initMap = async () => {
  mapLoading.value = true;
  try {
    const mapContainer = document.getElementById("task-route-map");
    if (
      mapContainer &&
      (mapContainer.offsetWidth === 0 || mapContainer.offsetHeight === 0)
    ) {
      mapContainer.style.minHeight = "400px";
      await nextTick();
    }

    const AMap = await loadAMapSDK().catch((error) => {
      throw new Error(`高德地图API加载失败: ${error.message || error}`);
    });

    AMapInstance = AMap;
    map = new AMap.Map("task-route-map", {
      zoom: 12,
      center: [103.8349, 36.068],
      mapStyle: "amap://styles/whitesmoke",
      viewMode: "2D",
      dragEnable: true,
      animateEnable: true,
    });

    map.addControl(
      new AMap.ToolBar({ position: "RB", offset: [10, 60], liteStyle: true })
    );
  } catch (error) {
    console.error("地图初始化失败:", error);
    message.error(error.message || "地图初始化失败，请刷新页面重试");
  } finally {
    mapLoading.value = false;
  }
};

// 绘制可拖拽的驾车路线
const drawRoute = () => {
  if (!map || !AMapInstance) {
    message.error("地图未初始化，请稍后重试");
    return;
  }
  if (!formData.originStopId) {
    message.error("请先选择起点");
    return;
  }
  if (!formData.destinationStopId) {
    message.error("请先选择终点");
    return;
  }

  const origin = getStopCoord(formData.originStopId);
  const destination = getStopCoord(formData.destinationStopId);

  if (!origin) {
    message.error("起点坐标无效，请检查站点经纬度");
    return;
  }
  if (!destination) {
    message.error("终点坐标无效，请检查站点经纬度");
    return;
  }

  // 构建路径：起点 → 途经点 → 终点
  const path = [origin];
  formData.waypoints.forEach((w) => {
    if (w.stopId) {
      const coord = getStopCoord(w.stopId);
      if (coord) path.push(coord);
    }
  });
  path.push(destination);

  routeLoading.value = true;

  // 清除地图上所有覆盖物（旧路线 + 自定义标记），控件不受影响
  map.clearMap();
  routeMarkers = [];
  dragRoute = null;
  redrawFences();

  try {
    dragRoute = new AMapInstance.DragRoute(
      map,
      path,
      AMapInstance.DrivingPolicy.LEAST_FEE
    );
    // search 回调获取路径规划返回数据
    dragRoute.search();
    routeLoading.value = false;
    message.success("路线绘制成功，可拖拽调整路线");

  } catch (error) {
    routeLoading.value = false;
    console.error("路线规划失败:", error);
    message.error("路线规划失败，请检查站点坐标是否正确");
  }
};

const handleVehicleSearch = (value) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    fetchVehicleList(value);
  }, 300);
};

// 获取车辆列表：sk 为空时也调用接口，让下拉框默认有数据
const fetchVehicleList = (sk) => {
  vehicleFetching.value = true;
  const data = {
    pageIndex: 1,
    pageSize: 20,
    conditionMap: { sk: sk || "", dept_id: store.deptId },
  };
  getVehiclePageList(data)
    .then((res) => {
      vehicleOptions.value = (res.data.records || []).map((v) => ({
        ...v,
        label: v.licenceNo || v.vin,
      }));
    })
    .finally(() => {
      vehicleFetching.value = false;
    });
};

const handleVehicleChange = (value) => {
  if (value) {
    const vehicle = vehicleOptions.value.find((v) => v.id === value);
    if (vehicle) {
      formData.vin = vehicle.vin || "";
      formData.licenceNo = vehicle.licenceNo || "";
    }
  } else {
    formData.vin = "";
    formData.licenceNo = "";
  }
};

// 加载运营企业列表
const loadOperatorList = async () => {
  try {
    const res = await getOperatorList({});
    if (res.code === 200 && res.data) {
      operatorOptions.value = res.data;
    }
  } catch (error) {
    console.error("获取运营企业列表失败:", error);
  }
};

// 加载城市列表
const loadCityList = async () => {
  try {
    const res = await getCityList();
    if (res.code === 200 && res.data) {
      cityOptions.value = res.data;
    }
  } catch (error) {
    console.error("获取城市列表失败:", error);
  }
};

// 加载安全员列表
const loadSecurityUserList = async () => {
  try {
    const res = await getSecurityUserList();
    if (res.code === 200 && res.data) {
      securityUserOptions.value = res.data;
    }
  } catch (error) {
    console.error("获取安全员列表失败:", error);
  }
};

// 加载站点列表（一次性加载，下拉使用本地按 name 过滤）
const loadStopList = async (name) => {
  try {
    const res = await getStopList(name);
    if (res.code === 200 && res.data) {
      stopOptions.value = res.data;
    }
  } catch (error) {
    console.error("获取站点列表失败:", error);
  }
};

const formData = reactive({
  vehicleId: undefined,
  cityId: undefined,
  operatorId: undefined,
  securityUserIds: [],
  description: "",
  operationDateRange: null,
  operationTimes: [{ start: null, end: null }],
  routeName: "",
  originStopId: undefined,
  destinationStopId: undefined,
  waypoints: [],
});

// 起点或终点变化时，自动拼接路线名称（回显期间跳过，避免覆盖已保存的名称）
watch(
  () => [formData.originStopId, formData.destinationStopId],
  () => {
    if (isEchoing) return;
    const startStop = stopOptions.value.find(
      (s) => s.id === formData.originStopId
    );
    const endStop = stopOptions.value.find(
      (s) => s.id === formData.destinationStopId
    );
    if (startStop && endStop) {
      formData.routeName = `${startStop.name}-${endStop.name}`;
    }
  }
);

const rules = {
  vehicleId: [{ required: true, message: "请选择车辆", trigger: "change" }],
  cityId: [{ required: true, message: "请选择城市", trigger: "change" }],
  operatorId: [{ required: true, message: "请选择运营企业", trigger: "change" }],
  securityUserIds: [
    {
      required: true,
      message: "请选择安全员",
      trigger: "change",
      type: "array",
    },
  ],
  operationDateRange: [
    {
      required: true,
      message: "请选择运营日期范围",
      trigger: "change",
      type: "array",
    },
  ],
  routeName: [{ required: true, message: "请输入路线名称", trigger: "change" }],
  originStopId: [{ required: true, message: "请选择起点", trigger: "change" }],
  destinationStopId: [{ required: true, message: "请选择终点", trigger: "change" }],
};

// 运营时段操作
const addTimeSlot = () => {
  formData.operationTimes.push({ start: null, end: null });
};

const removeTimeSlot = (idx) => {
  if (formData.operationTimes.length > 1) {
    formData.operationTimes.splice(idx, 1);
  }
};

// 途经点操作
const addWaypoint = () => {
  formData.waypoints.push({ stopId: undefined });
};

const removeWaypoint = (idx) => {
  formData.waypoints.splice(idx, 1);
  updateRouteMarkers();
};

const moveWaypoint = (idx, direction) => {
  const target = idx + direction;
  if (target < 0 || target >= formData.waypoints.length) return;
  const list = formData.waypoints;
  [list[idx], list[target]] = [list[target], list[idx]];
  updateRouteMarkers();
};

const goBack = () => {
  router.back();
};

// 绘制已保存的路线和点位标记（编辑回显时使用，参考 detail.vue 的 drawRouteAndMarkers）
const drawSavedRoute = (detail) => {
  if (!map || !AMapInstance || !detail) return;

  map.clearMap();
  dragRoute = null;
  routeMarkers = [];
  redrawFences();
  const points = [];
  const routeOverlays = [];

  // 绘制 paths 路线（带箭头的蓝色折线 + 白色发光底层）
  const paths = detail.paths || [];
  if (paths.length > 0) {
    new AMapInstance.Polyline({
      path: paths,
      strokeColor: "#ffffff",
      strokeWeight: 10,
      strokeOpacity: 0.6,
      lineJoin: "round",
      lineCap: "round",
      map: map,
      zIndex: 1,
    });
    const polyline = new AMapInstance.Polyline({
      path: paths,
      strokeColor: "#1890ff",
      strokeWeight: 6,
      strokeOpacity: 0.9,
      lineJoin: "round",
      lineCap: "round",
      showDir: true,
      map: map,
    });
    routeOverlays.push(polyline);
  }

  // 起点（绿色）使用 originStopGcj02Coordinates
  const originCoord = detail.originStopGcj02Coordinates;
  const originName = (detail.originStopName || "").trim();
  if (originCoord && Array.isArray(originCoord) && originCoord.length === 2) {
    const marker = new AMapInstance.Marker({
      position: originCoord,
      content: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;"><div style="width:28px;height:28px;border-radius:50%;background:#52c41a;color:#fff;font-size:12px;line-height:28px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">起</div><span style="font-size:12px;color:#333;background:rgba(255,255,255,0.9);padding:2px 6px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.2);">${originName}</span></div>`,
      offset: new AMapInstance.Pixel(-14, -14),
      map: map,
    });
    points.push(originCoord);
    routeOverlays.push(marker);
  }

  // 终点（红色）使用 destinationStopGcj02Coordinates
  const destCoord = detail.destinationStopGcj02Coordinates;
  const destName = (detail.destinationStopName || "").trim();
  if (destCoord && Array.isArray(destCoord) && destCoord.length === 2) {
    const marker = new AMapInstance.Marker({
      position: destCoord,
      content: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;"><div style="width:28px;height:28px;border-radius:50%;background:#ff4d4f;color:#fff;font-size:12px;line-height:28px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">终</div><span style="font-size:12px;color:#333;background:rgba(255,255,255,0.9);padding:2px 6px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.2);">${destName}</span></div>`,
      offset: new AMapInstance.Pixel(-14, -14),
      map: map,
    });
    points.push(destCoord);
    routeOverlays.push(marker);
  }

  // 途经点（蓝色带序号）使用 waypoints 元素的 gcj02Coordinates
  const wps = detail.waypoints || [];
  wps.forEach((wp, idx) => {
    const coord = wp.gcj02Coordinates;
    const wpName = (wp.stopName || wp.name || "").trim();
    if (coord && Array.isArray(coord) && coord.length === 2) {
      const marker = new AMapInstance.Marker({
        position: coord,
        content: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;"><div style="width:24px;height:24px;border-radius:50%;background:#1890ff;color:#fff;font-size:12px;line-height:24px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${idx + 1}</div><span style="font-size:12px;color:#333;background:rgba(255,255,255,0.9);padding:2px 6px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.2);">${wpName}</span></div>`,
        offset: new AMapInstance.Pixel(-12, -12),
        map: map,
      });
      points.push(coord);
      routeOverlays.push(marker);
    }
  });

  // 自适应视野：仅适配路线覆盖物，不包含围栏
  if (points.length > 0) {
    map.setFitView(routeOverlays, true, [80, 80, 80, 80]);
  }
};

// 加载任务详情并回显表单数据（参考 detail.vue 的 loadDetail）
const loadTaskDetail = async () => {
  const id = taskId.value;
  if (!id) return;
  try {
    const res = await getTaskDetail(id);
    if (res.code !== 200 || !res.data) {
      message.error(res.message || "获取任务详情失败");
      return;
    }
    const detail = res.data;

    // 开启回显标志，跳过路线名称自动拼接
    isEchoing = true;

    // 车辆：将详情中的车辆加入下拉选项，确保选中项可显示
    if (detail.vehicleId) {
      const exist = vehicleOptions.value.find((v) => v.id === detail.vehicleId);
      if (!exist) {
        vehicleOptions.value.unshift({
          id: detail.vehicleId,
          label: detail.licenceNo || detail.vin || "",
          licenceNo: detail.licenceNo || "",
          vin: detail.vin || "",
        });
      }
      formData.vehicleId = detail.vehicleId;
      formData.vin = detail.vin || "";
      formData.licenceNo = detail.licenceNo || "";
    }

    // 城市
    if (detail.cityId) {
      formData.cityId = detail.cityId;
    }

    // 运营企业
    if (detail.operatorId) {
      formData.operatorId = detail.operatorId;
    }

    // 安全员（详情返回 securityUsers: [{id, name}]，表单用 id 数组）
    if (Array.isArray(detail.securityUsers)) {
      formData.securityUserIds = detail.securityUsers
        .map((u) => u.id)
        .filter(Boolean);
    }

    // 运营日期范围（startDate/endDate 为 "YYYY-MM-DD" 字符串，转为 dayjs 对象）
    if (detail.startDate && detail.endDate) {
      formData.operationDateRange = [
        dayjs(detail.startDate),
        dayjs(detail.endDate),
      ];
    }

    // 运营时段（详情返回 JSON 字符串或数组，"HH:mm" 字符串转为 dayjs 对象）
    let times = detail.operationTimes;
    if (typeof times === "string") {
      try {
        times = JSON.parse(times);
      } catch (e) {
        times = [];
      }
    }
    if (Array.isArray(times) && times.length > 0) {
      formData.operationTimes = times.map((s) => ({
        start: s.start ? dayjs(s.start, "HH:mm") : null,
        end: s.end ? dayjs(s.end, "HH:mm") : null,
      }));
    }

    // 任务描述
    formData.description = detail.description || "";

    // 路线名称
    formData.routeName = detail.routeName || "";

    // 起点、终点
    formData.originStopId = detail.originStopId || undefined;
    formData.destinationStopId = detail.destinationStopId || undefined;

    // 途经点（详情返回 waypoints: [{stopId, stopName, gcj02Coordinates, sort}]，表单只需 stopId）
    if (Array.isArray(detail.waypoints)) {
      // 按 sort 排序后回显
      const sorted = [...detail.waypoints].sort(
        (a, b) => (a.sort || 0) - (b.sort || 0)
      );
      formData.waypoints = sorted.map((wp) => ({
        stopId: wp.stopId,
      }));
    }

    // 关闭回显标志，后续用户操作恢复正常
    await nextTick();
    isEchoing = false;

    // 在地图上绘制已保存的路线和点位
    drawSavedRoute(detail);
  } catch (error) {
    console.error("获取任务详情失败:", error);
    message.error("获取任务详情失败");
  }
};

const handleSubmit = async () => {
  try {
    await formRef.value.validate();
  } catch (e) {
    return;
  }

  // 校验运营时段
  const invalidSlot = formData.operationTimes.find(
    (s) => !s.start || !s.end
  );
  if (invalidSlot) {
    message.error("请补全运营时段");
    return;
  }
  for (const s of formData.operationTimes) {
    if (dayjs(s.start).isAfter(dayjs(s.end))) {
      message.error("运营时段的开始时间不能晚于结束时间");
      return;
    }
  }

  // 校验运营日期范围
  if (
    formData.operationDateRange &&
    formData.operationDateRange[0] &&
    formData.operationDateRange[1] &&
    dayjs(formData.operationDateRange[0]).isAfter(
      dayjs(formData.operationDateRange[1])
    )
  ) {
    message.error("运营开始日期不能晚于结束日期");
    return;
  }

  // 校验途经点不能为空且不能重复
  const emptyWaypoint = formData.waypoints.find((w) => !w.stopId);
  if (emptyWaypoint) {
    message.error("请选择途经点");
    return;
  }

  submitLoading.value = true;

  // 从路径规划返回数据中提取所有经纬度坐标集合，传给后端保存（后续详情/编辑回显使用）
  let paths = [];
  if (dragRoute && typeof dragRoute.getRoute === "function") {
    paths = dragRoute.getRoute() || [];
  }
  if (!paths || paths.length === 0) {
    message.error("请规划路径后再提交");
    return;
  }
  const payload = {
    vehicleId: formData.vehicleId,
    licenceNo: formData.licenceNo,
    vin: formData.vin,
    cityId: formData.cityId,
    operatorId: formData.operatorId,
    securityUserIds: formData.securityUserIds,
    description: formData.description,
    startDate: formData.operationDateRange?.[0]
      ? dayjs(formData.operationDateRange[0]).format("YYYY-MM-DD")
      : "",
    endDate: formData.operationDateRange?.[1]
      ? dayjs(formData.operationDateRange[1]).format("YYYY-MM-DD")
      : "",
    operationTimes: formData.operationTimes.map((s) => ({
      start: s.start ? dayjs(s.start).format("HH:mm") : "",
      end: s.end ? dayjs(s.end).format("HH:mm") : "",
    })),
    routeName: formData.routeName,
    originStopId: formData.originStopId,
    destinationStopId: formData.destinationStopId,
    waypoints: formData.waypoints.map((w, idx) => ({
      stopId: w.stopId,
      sort: idx + 1,
    })),
    paths,
  };

  // 编辑模式携带 taskId 并调用更新接口，新增模式调用新增接口
  const isEditMode = isEdit.value;
  if (isEditMode) {
    payload.id = taskId.value;
  }
  const res = isEditMode
    ? await updateTask(payload)
    : await addTask(payload);
  if (res.code === 200) {
    submitLoading.value = false;
    message.success(isEditMode ? "更新成功" : "新增成功");
  } else {
    submitLoading.value = false;
    message.error(res.msg || (isEditMode ? "更新失败" : "新增失败"));
  }
};

onMounted(async () => {
  // 默认加载车辆列表，使车辆编码下拉框默认有数据
  fetchVehicleList("");
  // 加载运营企业列表
  loadOperatorList();
  // 加载城市列表
  loadCityList();
  // 加载安全员列表
  loadSecurityUserList();
  // 加载站点列表，用于起点/途经点/终点下拉
  await loadStopList();
  // 初始化右侧地图
  await initMap();
  // 加载并绘制围栏（作为背景参考）
  loadFences();
  // 编辑模式：加载任务详情并回显表单（需在选项列表和地图初始化之后）
  if (isEdit.value) {
    await loadTaskDetail();
  }
});

onBeforeUnmount(() => {
  if (dragRoute) {
    dragRoute = null;
  }
  if (map) {
    map.destroy();
    map = null;
  }
});
</script>

<style lang="less" scoped>
.task-add-page {
  display: flex;
  flex-direction: column;
  padding: 16px;
  height: calc(100vh - 70px);
  background-color: #ffffff;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
  flex-shrink: 0;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
  }
}

.page-body {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.left-panel {
  width: 560px;
  flex-shrink: 0;
  overflow-y: auto;
  padding-right: 8px;

  .task-form {
    background: #fafafa;
    padding: 20px;
    border-radius: 8px;
  }
}

.right-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  position: relative;
  overflow: hidden;
  border-radius: 8px;

  .map-container {
    width: 100%;
    height: 100%;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    z-index: 10;

    .loading-text {
      font-size: 14px;
      color: #666;
    }
  }
}

.form-section {
  margin-bottom: 24px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
    padding-left: 10px;
    border-left: 3px solid #1890ff;
    line-height: 1;
  }

  .end-point-item {
    margin-top: 16px;
  }

  .point-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .point-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .start-dot {
      background: #52c41a;
    }

    .end-dot {
      background: #ff4d4f;
    }

    .point-select {
      flex: 1;
    }
  }

  .route-action {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;

    .route-tip {
      font-size: 13px;
      color: #999;
    }
  }
}

.form-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px dashed #e4e4e4;
  margin-top: 8px;
}

.time-slots {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .time-slot-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .time-separator {
    color: #999;
  }
}

.waypoint-block {
  .waypoint-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    .waypoint-label {
      font-size: 14px;
      color: #595959;
    }
  }

  .waypoint-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .waypoint-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 4px;

    .waypoint-index {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #1890ff;
      color: #fff;
      font-size: 12px;
      flex-shrink: 0;
    }

    .waypoint-input {
      flex: 1;
    }

    .waypoint-actions {
      display: flex;
      gap: 2px;
      flex-shrink: 0;

      .ant-btn {
        padding: 0 4px;
      }
    }
  }

  .empty-tip {
    padding: 16px;
    text-align: center;
    color: #999;
    font-size: 13px;
    background: #fff;
    border: 1px dashed #e4e4e4;
    border-radius: 4px;
  }
}

a {
  color: #1e8ef2;
}
</style>
